// client/src/components/pages/LexiStar/StatusBoard/OverrideDialog.jsx
//
// Manual override from the status board (FR-STAT-004, LS-09 §5.3 "a small form
// (status, reason, until)"). Calls POST /trips/:id:overrideStage
// (lexistar.trips.manage). The reason is mandatory — a reason code, plus a note
// when the code is "other"; "until" is optional (empty = until the engine's
// next derived transition).

import React, { useEffect, useState } from 'react';
import { FaTimes, FaHandPaper } from 'react-icons/fa';
import ThemedSelect from '../../../common/ThemedSelect';
import { overrideStage } from '../../../../services/lexistarStatusBoard';
import { OVERRIDE_STAGES, STAGE_LABELS } from './statusLanguage';
import { rowRegistration } from './columns';
import { inputClasses, DateTimeField, ReasonCodeSelect, StagePill, useReasonCodes } from './atoms';

export default function OverrideDialog({ isOpen, row, onClose, onDone }) {
  const codes = useReasonCodes('override');
  const [stage, setStage] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [until, setUntil] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setStage('');
      setReasonCode('');
      setNote('');
      setUntil(null);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen || !row?.trip) return null;
  const trip = row.trip;
  const registration = rowRegistration(row);

  const handleSubmit = async () => {
    if (!stage) {
      setError('Choose the status to set');
      return;
    }
    if (!reasonCode) {
      setError('A reason is required for every override');
      return;
    }
    if (reasonCode === 'other' && !note.trim()) {
      setError('Describe the reason when choosing "Other"');
      return;
    }
    const label = codes.find((c) => c.code === reasonCode)?.label || reasonCode;
    setSaving(true);
    setError(null);
    try {
      // The M2 API records a free-text reason (min 3 chars): code label + note.
      await overrideStage(trip.id, {
        stage,
        reason: note.trim() ? `${label}: ${note.trim()}` : label,
        until: until ? until.toISOString() : undefined,
      });
      onDone?.(`${registration}: ${trip.reference} set to ${STAGE_LABELS[stage]}.`);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Override failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Override ${registration}`}
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-visible"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">Manual override</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <FaTimes size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 flex items-center justify-between gap-2">
            <span>
              <span className="font-mono font-medium">{registration}</span>
              <span className="text-slate-500"> · {trip.reference}</span>
            </span>
            <StagePill stage={trip.stage} />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Set status to</label>
            <ThemedSelect
              value={stage}
              onChange={(e) => setStage(e.target.value)}
              className={inputClasses}
              aria-label="Set status to"
            >
              <option value="">Choose...</option>
              {OVERRIDE_STAGES.filter((s) => s !== trip.stage).map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABELS[s]}
                </option>
              ))}
            </ThemedSelect>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Reason</label>
            <ReasonCodeSelect codes={codes} value={reasonCode} onChange={setReasonCode} />
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                reasonCode === 'other' ? 'Describe the reason (required)' : 'Note (optional)'
              }
              aria-label="Override note"
              maxLength={200}
              className={`${inputClasses} mt-2`}
            />
          </div>

          <div>
            <label
              htmlFor="lexistar-override-until"
              className="block text-sm font-medium text-slate-700 mb-1"
            >
              Until
            </label>
            <DateTimeField
              id="lexistar-override-until"
              value={until}
              onChange={setUntil}
              minDate={new Date()}
              placeholder="Until the next derived change"
            />
            <p className="mt-1 text-xs text-slate-500">
              Overrides are audited and take precedence over the derived status while they last.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className={`inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow ${saving ? 'bg-slate-400 cursor-not-allowed' : 'bg-primary-500 hover:bg-primary-600'}`}
          >
            <FaHandPaper size={12} />
            {saving ? 'Saving...' : 'Override'}
          </button>
        </div>
      </div>
    </div>
  );
}
